// packages/web/src/api/auth.ts
/**
 * Auth API client — talks to /auth/* endpoints on the ingest worker.
 *
 * Separate from postV4/getV4 (api-client.ts) because:
 *   1. Auth requests need `credentials: 'include'` so the httpOnly session
 *      cookie travels (postV4/getV4 don't set this — they're for stateless
 *      v4 ingest calls that use X-Anon-Id header, not cookies).
 *   2. /auth/me returns null on 401 (not an error) — the app boots in either
 *      anonymous or authenticated state; 401 just means "no session yet".
 *
 * Spec: docs/architecture/account-system-design.md §7.1, §9.1.2
 */

import { INGEST_URL, IngestApiError } from '../lib/api-client.js';
import type { AuthUser } from '../lib/store/auth-slice.js';

export interface AuthApiResponse {
  user: AuthUser;
}

/** 19BO+ Phase B: signup request response — no user (created at verify stage). */
export interface SignupRequestResponse {
  ok: boolean;
  message?: string;
}

/**
 * Header set for auth requests. X-Requested-With is the CSRF defense header
 * (19BO+ CROSS-ORIGIN-AUTH): the backend rejects state-changing methods
 * without it. Harmless on GET. credentials:'include' is added per-call so the
 * httpOnly session cookie travels on the credentialed cross-origin request.
 */
const AUTH_HEADERS = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'fetch',
};

async function postAuth(path: string, body: unknown): Promise<AuthApiResponse> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'POST',
    headers: AUTH_HEADERS,
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown', message: 'Request failed' }));
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

/**
 * 19BO+ Phase B: POST that returns a generic JSON (not AuthApiResponse). Used
 * by signup (returns { ok, message }, no user). Same error contract as postAuth.
 */
async function postAuthRaw<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'POST',
    headers: AUTH_HEADERS,
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown', message: 'Request failed' }));
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

/**
 * 19BO+ CROSS-ORIGIN-AUTH: GET that returns { ok: true } on 2xx. Used by
 * signupVerify + magicVerify — both endpoints now return JSON (no redirect)
 * so the SPA fetch can read the Set-Cookie from the credentialed response.
 * Throws IngestApiError on 400/401/409.
 */
async function getAuthOk(path: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'GET',
    headers: AUTH_HEADERS,
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 409 || res.status === 400) {
    const json = await res.json().catch(() => ({ error: 'unknown' }));
    throw new IngestApiError(res.status, json);
  }
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }));
    throw new IngestApiError(res.status, json);
  }
  return { ok: true };
}

async function getAuth(path: string): Promise<AuthApiResponse | null> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'GET',
    headers: AUTH_HEADERS,
    credentials: 'include',
  });
  // 401 = no session — return null, don't throw. App boots to anonymous.
  if (res.status === 401) return null;
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }));
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

export const authApi = {
  me: () => getAuth('/auth/me'),
  /**
   * 19BO+ Phase B: signup is two-stage. This is the REQUEST stage — mints a
   * signup_verify token + sends a confirmation email. Returns { ok } (NO user
   * — the user is created at the verify stage after the email link is clicked).
   * Anti-enumeration: returns the same shape whether the email is fresh or
   * already registered.
   */
  signup: (email: string, password: string, displayName?: string, inviteCode?: string): Promise<SignupRequestResponse> =>
    postAuthRaw('/auth/signup', { email, password, displayName, inviteCode }),
  login: (email: string, password: string) =>
    postAuth('/auth/login', { email, password }),
  logout: () => postAuth('/auth/logout', {}),
  changePassword: (currentPassword: string, newPassword: string) =>
    postAuth('/auth/change-password', { currentPassword, newPassword }),
  /**
   * Request a magic link. 19BN Phase D D3: `purpose` selects login (default)
   * vs reset_password. The backend ignores unknown values and falls back to
   * login, so omitting purpose preserves the original magic-link behavior.
   */
  magicRequest: (email: string, purpose?: 'login' | 'reset_password') =>
    postAuth('/auth/magic/request', purpose ? { email, purpose } : { email }),
  /**
   * Reset password using a reset_password magic token. 19BN Phase D D3.
   * Throws IngestApiError on 401 (invalid/expired token) or 400 (weak pw).
   */
  resetPassword: (token: string, newPassword: string) =>
    postAuth('/auth/reset-password', { token, newPassword }),
  /**
   * 19BO+ Phase B: verify a signup_verify token (stage 2 of two-stage signup).
   * The server validates the token, creates the user + grants signup bonus,
   * and sets the session cookie via Set-Cookie on the JSON response.
   * Returns { ok: true } on success (the caller then probes /auth/me to load
   * the new user). Throws IngestApiError on 401 (invalid/expired/used token)
   * or 409 (email already registered via a concurrent verify).
   *
   * 19BO+ CROSS-ORIGIN-AUTH: the endpoint now returns JSON (no redirect) so
   * the SPA fetch can read the Set-Cookie from the credentialed cross-origin
   * response.
   */
  signupVerify: (token: string): Promise<{ ok: boolean }> =>
    getAuthOk(`/auth/signup/verify?token=${encodeURIComponent(token)}`),
  /**
   * 19BO+ CROSS-ORIGIN-AUTH: verify a login magic token. The server validates
   * the token + creates a session, returning { ok: true } + Set-Cookie. Same
   * JSON contract as signupVerify. The SPA reads ?magic-token= from the email
   * link and calls this. Throws IngestApiError on 401 (invalid/expired/used)
   * or 429 (account locked).
   */
  magicVerify: (token: string): Promise<{ ok: boolean }> =>
    getAuthOk(`/auth/magic/verify?token=${encodeURIComponent(token)}`),
};
